import { useState } from 'react';
import { Link, useSearchParams } from 'react-router';
import { resetPassword } from '../api/auth';
import { ApiError } from '../api/client';

const labelStyle = {
  fontFamily: "'Barlow Condensed', sans-serif",
  color: 'rgba(147,197,253,0.7)',
  letterSpacing: '0.12em',
} as const;

const inputStyle = {
  background: 'rgba(255,255,255,0.07)',
  border: '1px solid rgba(96,200,232,0.2)',
  padding: '11px 14px',
  color: 'white',
} as const;

export function ResetPassword() {
  const [searchParams] = useSearchParams();
  const token = searchParams.get('token');

  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [error, setError] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [done, setDone] = useState(false);

  async function handleSubmit(e: React.FormEvent) {
    e.preventDefault();
    setError('');

    if (!token) {
      setError('This reset link is missing its token. Request a new one.');
      return;
    }
    if (newPassword.length < 8) {
      setError('Password must be at least 8 characters.');
      return;
    }
    if (newPassword !== confirmPassword) {
      setError('Passwords do not match.');
      return;
    }

    setSubmitting(true);
    try {
      await resetPassword(token, newPassword);
      setDone(true);
    } catch (caught) {
      setError(caught instanceof ApiError ? caught.message : 'Something went wrong. Please try again.');
    } finally {
      setSubmitting(false);
    }
  }

  return (
    <div className="relative min-h-screen overflow-hidden" style={{ fontFamily: "'Barlow', sans-serif" }}>
      <div className="absolute inset-0 bg-slate-950">
        <img
          src="https://images.unsplash.com/photo-1687428896920-fae2683df1f3?w=1800&h=1200&fit=crop&auto=format"
          alt="Lake surrounded by rocky mountains under cloudy sky"
          className="w-full h-full object-cover"
          style={{ objectPosition: '50% 35%', opacity: 0.85 }}
        />
      </div>

      <div className="absolute inset-0" style={{ background: 'linear-gradient(to bottom, rgba(2,6,12,0.78) 0%, rgba(2,6,12,0.35) 28%, transparent 48%)' }} />
      <div className="absolute inset-0" style={{ background: 'linear-gradient(to top, rgba(2,6,12,0.88) 0%, rgba(2,6,12,0.3) 30%, transparent 50%)' }} />

      <div
        className="absolute inset-x-0 top-0 flex flex-col items-center justify-center"
        style={{ height: '47%', paddingTop: '2vh' }}
      >
        <h1
          className="uppercase leading-none text-center"
          style={{
            fontFamily: "'Cinzel', serif",
            fontWeight: 600,
            fontSize: 'clamp(1.6rem, 5vw, 3.2rem)',
            color: '#ffffff',
            textShadow: '0 2px 48px rgba(0,0,0,0.9), 0 0 80px rgba(0,0,0,0.6)',
            letterSpacing: '0.06em',
            width: '100vw',
          }}
        >
          Black Rock
        </h1>
        <p
          className="uppercase mt-1 text-center"
          style={{
            fontFamily: "'Barlow Condensed', sans-serif",
            fontWeight: 700,
            fontSize: 'clamp(1.4rem, 3.5vw, 2.4rem)',
            letterSpacing: '0.24em',
            background: 'linear-gradient(90deg, #93c5fd 0%, #60c8e8 50%, #93c5fd 100%)',
            WebkitBackgroundClip: 'text',
            WebkitTextFillColor: 'transparent',
            filter: 'drop-shadow(0 0 16px rgba(77,217,247,0.5))',
          }}
        >
          Curling
        </p>
      </div>

      <div className="absolute inset-x-0 bottom-0 flex flex-col items-center justify-center" style={{ height: '72%' }}>
        <div
          className="w-full"
          style={{
            maxWidth: '360px',
            margin: '0 24px',
            background: 'rgba(4,12,24,0.7)',
            backdropFilter: 'blur(20px)',
            WebkitBackdropFilter: 'blur(20px)',
            border: '1px solid rgba(96,200,232,0.22)',
            borderRadius: '16px',
            padding: '32px 28px',
            boxShadow: '0 8px 40px rgba(0,0,0,0.5)',
          }}
        >
          {done ? (
            <div className="space-y-4 text-center">
              <p className="text-sm" style={{ color: 'rgba(224,247,255,0.9)' }}>
                Your password has been changed.
              </p>
              <Link
                to="/"
                className="inline-block text-xs uppercase tracking-wider"
                style={{ fontFamily: "'Barlow Condensed', sans-serif", color: 'rgba(147,197,253,0.6)', letterSpacing: '0.15em' }}
              >
                Sign in
              </Link>
            </div>
          ) : (
            <form onSubmit={handleSubmit} className="space-y-4">
              <p className="text-sm" style={{ color: 'rgba(224,247,255,0.7)' }}>
                Choose a new password.
              </p>

              <div>
                <label className="block text-xs uppercase tracking-wider mb-2" style={labelStyle}>
                  New password
                </label>
                <input
                  type="password"
                  value={newPassword}
                  onChange={e => { setNewPassword(e.target.value); setError(''); }}
                  className="w-full rounded-lg text-white text-sm focus:outline-none"
                  style={inputStyle}
                  placeholder="At least 8 characters"
                  autoComplete="new-password"
                />
              </div>

              <div>
                <label className="block text-xs uppercase tracking-wider mb-2" style={labelStyle}>
                  Confirm password
                </label>
                <input
                  type="password"
                  value={confirmPassword}
                  onChange={e => { setConfirmPassword(e.target.value); setError(''); }}
                  className="w-full rounded-lg text-white text-sm focus:outline-none"
                  style={inputStyle}
                  placeholder="Re-enter password"
                  autoComplete="new-password"
                />
              </div>

              {error && <p className="text-red-400 text-sm">{error}</p>}

              <button
                type="submit"
                disabled={submitting}
                className="w-full py-3 rounded-lg uppercase tracking-wider transition-all duration-200 mt-2 disabled:opacity-60"
                style={{
                  fontFamily: "'Barlow Condensed', sans-serif",
                  fontSize: '0.95rem',
                  letterSpacing: '0.15em',
                  background: 'linear-gradient(135deg, rgba(77,217,247,0.22) 0%, rgba(59,130,246,0.32) 100%)',
                  border: '1px solid rgba(96,200,232,0.38)',
                  color: '#e0f7ff',
                  fontWeight: 700,
                }}
              >
                {submitting ? 'Saving…' : 'Set New Password'}
              </button>

              <Link
                to="/"
                className="block w-full text-center py-2 rounded-lg uppercase tracking-wider transition-all duration-200"
                style={{
                  fontFamily: "'Barlow Condensed', sans-serif",
                  fontSize: '0.75rem',
                  letterSpacing: '0.15em',
                  color: 'rgba(147,197,253,0.6)',
                  fontWeight: 600,
                }}
              >
                Back to sign in
              </Link>
            </form>
          )}
        </div>
      </div>
    </div>
  );
}
